import React, { useState } from 'react';
import { Upload, Eye, EyeSlash, CloudArrowUp } from 'react-bootstrap-icons';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { db, storage, auth } from '../config/firebase';
import '../styles/BloodRegistration.css';

const BloodRegistration = () => {
  const [formData, setFormData] = useState({
    hospitalName: '',
    licenseNumber: '',
    emailAddress: '',
    phoneNumber: '',
    address: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PDF, JPG, or PNG');
      return;
    }
    
    setSelectedFile(file);
    setError('');
  };

  const uploadFileToStorage = async (file, hospitalName, userId) => {
    try {
      const timestamp = Date.now();
      const safeName = hospitalName.replace(/[^a-zA-Z0-9]/g, '_');
      const ext = file.name.split('.').pop();
      const fileName = `${safeName}_${userId}_${timestamp}.${ext}`;
      const storageRef = ref(storage, `blood-bank-documents/${fileName}`);
      
      const metadata = {
        contentType: file.type,
        customMetadata: {
          hospitalName,
          userId,
          originalFilename: file.name,
          purpose: 'blood_bank_license'
        }
      };

      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        fileName: file.name,
        storagePath: snapshot.ref.fullPath,
        downloadURL,
        fileSize: file.size,
        fileType: file.type
      };
    } catch (error) {
      console.error('File upload error:', error);
      throw new Error('File upload failed. Please try again.');
    }
  };

  const validateForm = () => {
    const { hospitalName, licenseNumber, emailAddress, phoneNumber, address, password, confirmPassword } = formData;
    
    if (!hospitalName || !licenseNumber || !emailAddress || !phoneNumber || !address || !password || !confirmPassword) {
      setError('All fields are required');
      return false;
    }
    
    if (hospitalName.trim().length < 3) {
      setError('Hospital name must be at least 3 characters');
      return false;
    }
    
    if (licenseNumber.trim().length < 5) {
      setError('License number must be at least 5 characters');
      return false;
    }
    
    if (!/^\S+@\S+\.\S+$/.test(emailAddress)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) {
      setError('Please enter a valid phone number (10-15 digits)');
      return false;
    }
    
    if (address.trim().length < 10) {
      setError('Address must be at least 10 characters');
      return false;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain uppercase, lowercase letters and numbers');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (!selectedFile) {
      setError('Please upload a license document');
      return false;
    }
    
    return true;
  };

  const resetForm = () => {
    setFormData({
      hospitalName: '',
      licenseNumber: '',
      emailAddress: '',
      phoneNumber: '',
      address: '',
      password: '',
      confirmPassword: ''
    });
    setSelectedFile(null);
    const fileInput = document.getElementById('fileInput');
    if (fileInput) fileInput.value = '';
  };

  const registerBloodBank = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 1. Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.emailAddress.trim(),
        formData.password
      );
      const user = userCredential.user;

      // 2. Update user profile
      await updateProfile(user, {
        displayName: formData.hospitalName.trim()
      });

      // 3. Send email verification
      await sendEmailVerification(user);

      // 4. Upload license document
      const licenseDoc = await uploadFileToStorage(
        selectedFile,
        formData.hospitalName.trim(),
        user.uid
      );

      // 5. Create Firestore document
      const bloodBankData = {
        userId: user.uid,
        hospitalName: formData.hospitalName.trim(),
        licenseNumber: formData.licenseNumber.trim(),
        email: formData.emailAddress.trim().toLowerCase(),
        phone: formData.phoneNumber.trim(),
        address: formData.address.trim(),
        licenseDocument: {
          url: licenseDoc.downloadURL,
          path: licenseDoc.storagePath,
          fileName: licenseDoc.fileName
        },
        status: 'pending',
        emailVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'bloodBanks'), bloodBankData);

      // 6. Success
      setSuccess(`
        Registration successful!
        • Please verify your email (check your inbox)
        • Your account will be activated within 1-3 business days after verification
      `);
      resetForm();

    } catch (error) {
      console.error('Registration error:', error);
      
      // Clean up user if registration failed
      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
        } catch (deleteError) {
          console.error('Error cleaning up user:', deleteError);
        }
      }
      
      // User-friendly error messages
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered');
          break;
        case 'auth/weak-password':
          setError('Password must be at least 8 characters');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'permission-denied':
          setError('Operation not allowed. Please contact support');
          break;
        default:
          setError('Registration failed. Please try again later');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await registerBloodBank();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      const mockEvent = { target: { files: [file] } };
      handleFileSelect(mockEvent);
    }
  };

  return (
    <div className="blood-bank-container">
      <div className="main-card">
        <div className="card-body">
          <h2 className="page-title">Blood Bank Registration</h2>
          <p className="subtitle">Register your blood bank to join our network</p>
          
          {error && (
            <div className="alert alert-error" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}
          
          {success && (
            <div className="alert alert-success" role="alert">
              <strong>Success:</strong> {success}
            </div>
          )}
          
          <div className="content-row">
            <div className="form-section">
              <form className="form-wrapper" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label className="form-label" htmlFor="hospitalName">
                    Hospital/Blood Bank Name *
                  </label>
                  <input
                    id="hospitalName"
                    type="text"
                    name="hospitalName"
                    value={formData.hospitalName}
                    onChange={handleInputChange}
                    className="custom-input"
                    placeholder="Enter hospital or blood bank name"
                    required
                    maxLength="100"
                    autoComplete="organization"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="licenseNumber">
                    License Number *
                  </label>
                  <input
                    id="licenseNumber"
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    className="custom-input"
                    placeholder="Enter medical license number"
                    required
                    maxLength="50"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="emailAddress">
                    Email Address *
                  </label>
                  <input
                    id="emailAddress"
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                    className="custom-input"
                    placeholder="Enter official email address"
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="phoneNumber">
                    Phone Number *
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="custom-input"
                    placeholder="Enter contact phone number"
                    required
                    autoComplete="tel"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="address">
                    Complete Address *
                  </label>
                  <textarea
                    id="address"
                    rows={3}
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="custom-input"
                    placeholder="Enter complete address with city, state, and postal code"
                    required
                    maxLength="500"
                    autoComplete="street-address"
                  />
                </div>

                <div className="password-row">
                  <div className="password-col">
                    <div className="form-group">
                      <label className="form-label" htmlFor="password">
                        Password *
                      </label>
                      <div className="password-input-wrapper">
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="custom-input password-input"
                          placeholder="Create a strong password (8+ chars, letters & numbers)"
                          required
                          minLength="8"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeSlash /> : <Eye />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="password-col">
                    <div className="form-group">
                      <label className="form-label" htmlFor="confirmPassword">
                        Confirm Password *
                      </label>
                      <div className="password-input-wrapper">
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="custom-input password-input"
                          placeholder="Confirm your password"
                          required
                          minLength="8"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? <EyeSlash /> : <Eye />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="create-account-btn" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner" aria-hidden="true"></span>
                      Processing Registration...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>
            </div>

            <div className="upload-section">
              <div className="upload-area">
                <div className="upload-icon-wrapper">
                  <Upload className="upload-icon" />
                </div>
                <h5 className="upload-title">Upload License Document</h5>
                <p className="upload-subtitle">
                  Medical License or Registration Certificate
                </p>
                
                <div 
                  className="drag-drop-area"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <CloudArrowUp className="cloud-icon" />
                  <p className="drag-text">
                    Drag and drop your Document Here<br />
                    <span className="supported-formats">Supported: PDF, JPG, PNG | Max: 5MB</span>
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    className="file-input"
                    id="fileInput"
                    required
                  />
                  <button 
                    type="button"
                    className="select-file-btn"
                    onClick={() => document.getElementById('fileInput').click()}
                  >
                    Select File
                  </button>
                </div>
                
                {selectedFile && (
                  <div className="file-selected-alert">
                    <div className="file-info">
                      <strong>Selected:</strong> {selectedFile.name}<br />
                      <small>Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</small>
                      <small>Type: {selectedFile.type}</small>
                    </div>
                    <button 
                      type="button" 
                      className="remove-file-btn"
                      onClick={() => {
                        setSelectedFile(null);
                        const fileInput = document.getElementById('fileInput');
                        if (fileInput) fileInput.value = '';
                      }}
                      aria-label="Remove selected file"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="recommendation-box">
                <h6>Required Documents:</h6>
                <ul>
                  <li>Valid Medical/Blood Bank License</li>
                  <li>Registration Certificate</li>
                  <li>Government-issued ID (if required)</li>
                </ul>
                <p className="note-text">
                  <strong>Note:</strong> All documents will be verified before account activation. 
                  This process typically takes 1-3 business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BloodRegistration;